import React, { useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useClickOutside } from '@/hooks';
import { AdCampaignType } from '@/types';

type Option = {
  value: AdCampaignType;
  title: string;
  description: string;
  preview: React.ReactNode;
};

const options: Option[] = [
  {
    value: 'VIDEO',
    title: 'Video',
    description: 'Reproduccion a pantalla completa.',
    preview: (
      <div className="relative h-10 w-14 overflow-hidden border border-gray-200 bg-gradient-to-br from-slate-900 via-slate-700 to-slate-500">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
            <div className="ml-0.5 h-0 w-0 border-b-[4px] border-l-[6px] border-t-[4px] border-b-transparent border-l-white border-t-transparent" />
          </div>
        </div>
      </div>
    ),
  },
  {
    value: 'BANNER',
    title: 'Cintillo',
    description: 'Imagen superpuesta sobre la transmision.',
    preview: (
      <div className="relative h-10 w-14 overflow-hidden border border-gray-200 bg-gradient-to-br from-slate-200 via-slate-100 to-white">
        <div className="absolute inset-x-0 bottom-0 h-3 bg-slate-900/85" />
      </div>
    ),
  },
  {
    value: 'TEXT',
    title: 'Texto desplazable',
    description: 'Texto desplazable para mensajes rápidos.',
    preview: (
      <div className="relative h-10 w-14 overflow-hidden border border-gray-200 bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200">
        <div className="absolute inset-x-0 bottom-0 h-3 bg-red-700">
          <div className="mx-1 mt-1 h-1 w-8 bg-white/80" />
        </div>
      </div>
    ),
  },
  {
    value: 'NEWS_TICKER',
    title: 'News ticker',
    description: 'Lista de noticias o fuente RSS desplazable.',
    preview: (
      <div className="relative h-10 w-14 overflow-hidden border border-gray-200 bg-slate-950">
        <div className="absolute inset-x-0 bottom-0 flex h-3 items-center bg-gradient-to-r from-red-700 via-slate-100 to-slate-300">
          <div className="h-full w-4 bg-red-800" />
          <div className="ml-1 h-1 w-7 bg-slate-700/70" />
        </div>
      </div>
    ),
  },
];

const CampaignTypeCards = ({
  value,
  onChange,
  label = 'Tipo de campaña',
}: {
  value: AdCampaignType | null;
  onChange: (v: AdCampaignType) => void;
  label?: string;
  helperText?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setIsOpen(false));

  const selectedOption = options.find((option) => option.value === value) ?? options[0];

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="rounded-lg flex w-full items-center justify-between border border-gray-300 bg-white px-3 py-1.5 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <span className="flex min-h-[2rem] flex-1 items-center gap-3 overflow-hidden">
            {selectedOption.preview}
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-gray-900">{selectedOption.title}</span>
              <span className="block truncate text-xs text-gray-500">{selectedOption.description}</span>
            </span>
          </span>
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-20 mt-1 w-full border border-gray-200 bg-white shadow-lg">
            <ul className="py-1">
              {options.map((option) => {
                const isSelected = option.value === selectedOption.value;
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      className={`flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 ${
                        isSelected ? 'bg-indigo-50' : 'bg-white'
                      }`}
                      onClick={() => {
                        onChange(option.value);
                        setIsOpen(false);
                      }}
                    >
                      {option.preview}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-gray-900">{option.title}</div>
                        <div className="truncate text-xs text-gray-500">{option.description}</div>
                      </div>
                      <span className="flex h-4 w-4 items-center justify-center text-indigo-600">
                        {isSelected ? <Check size={14} /> : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignTypeCards;
